import { Component, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { ManagerReasonService } from '../../services/manager-reason.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SessionService } from '../../../../shared/services/session.service';
import { PartnerService } from '../../services/partner.service';
import { IPartner } from '../../data/partner';
import { ToastService } from '../../../../shared/services/toast.service';
import { EReasonType } from '../../data/reason-type.enum';
import { CompanyService } from '../../../companies/services/company.service';
import { ICompany } from '../../../companies/models/company.interface';
import { canPickCompany } from '../../../../shared/utilities/company-scope';

@Component({
  selector: 'app-add-new-reason',
  templateUrl: './add-new-reason.component.html',
  styleUrls: ['./add-new-reason.component.scss']
})
export class AddReasonComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  formOpen = false;

  formReason = new UntypedFormGroup({
    description: new UntypedFormControl('', [Validators.required]),
    partnerId: new UntypedFormControl(null, [Validators.required]),
    type: new UntypedFormControl(null, [Validators.required]),
    companyIds: new UntypedFormControl([], [Validators.required])
  });

  partners: IPartner[] = [];
  companies: ICompany[] = [];
  @Output() reloadReasons = new EventEmitter();

  typeOptions = [EReasonType.Atendimento, EReasonType.Cancelamento];

  // Usuário de empresa vincula o motivo só à própria, sem seletor; interno e parceiro sem empresa escolhem.
  podeEscolherEmpresa = false;
  private empresaFixa?: string;
  private allCompanies: ICompany[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly sessionService: SessionService,
    private readonly managerReasonService: ManagerReasonService,
    private readonly partnerService: PartnerService,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    const session = this.sessionService.getSession();
    const partnerId = session?.partnerId;
    this.podeEscolherEmpresa = canPickCompany(session);
    this.empresaFixa = this.podeEscolherEmpresa ? undefined : session?.companyId;

    if (partnerId) {
      this.formReason.get('partnerId')?.setValue(partnerId);
      this.formReason.get('partnerId')?.disable();
    } else {
      this.partnerService.listActive().subscribe((response) => {
        this.partners = response.data;
      });
    }

    // Empresas é required: sem a lista o formulário fica intransponível,
    // então o erro precisa ser avisado em vez de deixar o campo vazio sem explicação.
    this.companyService.listarEmpresasAtivas().subscribe({
      next: (response) => {
        this.allCompanies = response.data ?? [];
        this.filterCompaniesByPartner();
      },
      error: () => this.toastService.toast('Não foi possível carregar as empresas.')
    });

    // Company/ListActive só é escopado por parceiro quando o token carrega a claim;
    // para o Master a lista vem completa, então filtramos pelo parceiro escolhido.
    this.formReason
      .get('partnerId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.formReason.get('companyIds')?.setValue([]);
        this.filterCompaniesByPartner();
      });

    this.aplicarEmpresaFixa();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveReason(): void {
    if (this.formReason.invalid) {
      this.formReason.markAllAsTouched();
      return;
    }

    // O vínculo com as empresas é criado pelo próprio Create, no mesmo commit do motivo.
    // Não usar addReasonToCompany aqui: aquele endpoint substitui todos os motivos da empresa.
    this.managerReasonService.createReason(this.formReason.getRawValue()).subscribe((response) => {
      if (response.messages && response.messages.length > 0) {
        this.toastService.toast(response.messages);
      }

      if (response.isSucceed) {
        this.formOpen = false;
        this.resetForm();
        this.reloadReasons.emit();
      }
    });
  }

  private resetForm(): void {
    this.formReason.reset({ companyIds: [] });

    // reset() também limpa o control desabilitado; restaura o parceiro da sessão.
    const partnerId = this.sessionService.getSession()?.partnerId;

    if (partnerId) {
      this.formReason.get('partnerId')?.setValue(partnerId);
    }

    this.filterCompaniesByPartner();
    this.aplicarEmpresaFixa();
  }

  // Depois de definir o parceiro: a troca de parceiro limpa as empresas escolhidas.
  private aplicarEmpresaFixa(): void {
    if (this.empresaFixa) {
      this.formReason.get('companyIds')?.setValue([this.empresaFixa]);
    }
  }

  private filterCompaniesByPartner(): void {
    const partnerId = this.formReason.getRawValue().partnerId;

    this.companies = partnerId
      ? this.allCompanies.filter((company) => company.partnerId === partnerId)
      : this.allCompanies;
  }
}
